import { PatientRecordSearch as PatientRecordSearchFqs } from '@ctw/shared/content/patient-record-search/patient-record-search';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { useBrowserCompatibility } from '@ctw/shared/hooks/use-browser-compatibility';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';

export const PatientRecordSearchBar = () => {
  const { isSearchModalOpen, openSearch, closeSearch } = useSearchModal();
  const [isHovered, setIsHovered] = useState(false);
  const hasPatientRecordSearchFeature = useFeatureFlag('ctw-patient-record-search-fqs');
  const { breakpoints } = useBreakpointQuery();
  const { isMacOs } = useBrowserCompatibility();

  const onGlobalKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const isShortcut =
        (event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K');
      if (isShortcut) {
        event.preventDefault();
        if (!isSearchModalOpen) {
          openSearch();
        } else {
          closeSearch();
        }
      }
    },
    [isSearchModalOpen, openSearch, closeSearch],
  );

  useEffect(() => {
    window.addEventListener('keydown', onGlobalKeyDown);
    return () => window.removeEventListener('keydown', onGlobalKeyDown);
  }, [onGlobalKeyDown]);

  if (!hasPatientRecordSearchFeature) {
    return null;
  }

  if (!breakpoints.isAtLeast.md) {
    return (
      <button
        type="button"
        onClick={openSearch}
        className={tw`flex items-center justify-center rounded-md p-2 text-content-icon hover:bg-background-hover`}
        aria-label="Open chart search"
      >
        <FontAwesomeIcon icon={faSearch} className={tw`h-4 w-4`} />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="open-chart-search-button"
      onClick={openSearch}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={twx(
        'group flex h-8 w-fit items-center gap-2 rounded-md border px-3 text-sm transition-all duration-150',
        {
          'border-transparent bg-background-disabled': !isHovered,
          'border-border-main bg-white shadow-sm': isHovered,
        },
      )}
      aria-label="Open chart search"
    >
      <FontAwesomeIcon icon={faSearch} className={tw`h-3.5 w-3.5 text-content-icon`} />
      <span className={tw`flex-1 text-left text-content-subtle`}>Search the ZAP</span>
      <span
        className={twx('flex gap-1 transition-opacity duration-150', {
          'opacity-0': !isHovered,
          'opacity-100': isHovered,
        })}
      >
        <kbd
          className={tw`rounded border border-border-main bg-background-subtle px-1 py-0.5 font-sans text-content-subtle text-xs`}
        >
          {isMacOs ? '⌘' : 'Ctrl'}
        </kbd>
        <kbd
          className={tw`rounded border border-border-main bg-background-subtle px-1 py-0.5 font-sans text-content-subtle text-xs`}
        >
          K
        </kbd>
      </span>
    </button>
  );
};

const useSearchModal = () => {
  const { navigate } = useZuiContext();
  const telemetry = useTelemetry();
  const { openModal, closeModal, isModalOpen } = useModal({
    boundaryName: 'SearchModal',
    layout: {
      mode: 'fit',
      overflowBehavior: 'scroll',
      fullbleed: true,
      showCloseButton: false,
    },
    onModalEvent: (eventType) => {
      if (eventType === 'close') {
        navigate(
          pathWithParameters(
            window.location.pathname,
            {
              documentSearchQuery: null,
            },
            ['builderId'],
          ),
        );
      }
    },
  });

  const hasPatientRecordSearchFeature = useFeatureFlag('ctw-patient-record-search-fqs');
  return {
    isSearchModalOpen: isModalOpen,
    openSearch: () => {
      openModal({
        telemetry,
        action: {
          content: {
            body: hasPatientRecordSearchFeature && <PatientRecordSearchFqs />,
          },
        },
      });
    },
    closeSearch: () => {
      closeModal();
    },
  };
};
